import { getAccessToken } from '../authentication/supabaseService';

export type SubscriptionPlan = 'monthly' | 'yearly';

export interface StripeCheckoutSession {
  url: string;
  sessionId: string;
}

export interface SubscriptionPricing {
  setup_fee: number;            // in cents
  price_per_restaurant: number; // monthly, in cents
}

/**
 * Create a Stripe checkout session and get the redirect URL
 */
export async function createStripeCheckoutSession(
  plan: SubscriptionPlan,
  restaurantIds: string[]
): Promise<StripeCheckoutSession> {
  const token = await getAccessToken();

  // Convert restaurant IDs array to comma-separated string
  const restaurantIdsParam = restaurantIds.join(',');

  const res = await fetch(`/api/subscriptions/getStripeSession?plan=${plan}&restaurantIds=${restaurantIdsParam}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to create checkout session: ${res.statusText}`);
  }

  return (await res.json()) as StripeCheckoutSession;
}

/**
 * Get resolved pricing for the current user (custom or defaults)
 */
export async function getSubscriptionPricing(): Promise<SubscriptionPricing> {
  const token = await getAccessToken();

  const res = await fetch('/api/subscriptions/pricing', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch pricing: ${res.statusText}`);
  }

  return (await res.json()) as SubscriptionPricing;
}

/**
 * Update subscription quantity by +1 or -1
 */
export async function updateSubscriptionQuantity(change: 1 | -1): Promise<{ success: boolean; newQuantity: number }> {
  const token = await getAccessToken();

  const res = await fetch('/api/subscriptions/updateQuantity', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ change }),
  });

  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.error || `Failed to update subscription quantity: ${res.statusText}`);
  }

  return await res.json();
}
